/**
 * Per-merchant Sovrn affiliate click-out policy.
 *
 * Sovrn linking was removed product-wide in 2026-06 (PR #135) after its
 * broker-rotation dropped deep links on ~20% of clicks (catalog-weighted).
 * On 2026-07-06 the account's "CPC merchant programs" toggle was turned OFF,
 * which removed the deep-link-destroying CPC arbitrage partners from the
 * rotation, and a 3-run real-browser measurement (June baseline, top-30
 * re-run, 100-merchant long-tail sweep, post-toggle verification — reports in
 * kate629/thea scripts/output/aff_measure_*.json) confirmed per-merchant
 * behavior. Sovrn linking is re-enabled ONLY for merchants measured at 100%
 * deep-link survival; everything else clicks through to the plain merchant
 * URL. A daily canary (kate629/thea scripts/sovrn_canary.py) re-measures the
 * panel so a partner-mix change on Sovrn's side surfaces in days, not weeks.
 * The canary parses BOTH sets below from this file on origin/master:
 * SOVRN_ALLOWED_HOSTS is the pass/fail panel, SOVRN_BROKEN_HOSTS doubles as
 * its watch list (broken merchants it keeps clicking so a Sovrn-side fix
 * fires a "GOOD NEWS — re-add?" alert). Moving a host between the two sets
 * is the ONLY bookkeeping needed in either direction.
 */

/** Merchants measured at 100% deep-link survival (2026-07-06; nordstrom.com
 *  re-measured clean and re-added 2026-07-14; the 13 dog-birthday-guide
 *  merchants measured 100% at 80/80 clicks and added 2026-07-27).
 *  NOTE: amazon.com + a.co were REMOVED 2026-08-24 — Amazon is now monetized
 *  through our own Amazon Associates tag (givethea-20), applied at click-out in
 *  openExternal/amazonAffiliate.ts, never through Sovrn. Do not re-add them.
 *  2026-10-08: 129 catalog merchants that Sovrn DOES pay on — a US CPA program on
 *  the approved-merchants rate card AND a traced redirect that passes through an
 *  affiliate network — that nothing had added here. 1,032/1,032 cold clicks
 *  correct, at least 8 per merchant (kate629/thea
 *  scripts/output/aff_measure_paying_merchants_2026-10-08.json).
 *  Survival alone is not a reason to wrap: a merchant with no Sovrn US program
 *  "survives" because nothing touches the link, and earns $0. Check first with
 *  kate629/thea scripts/sovrn_program_check.py.
 *  2026-10-26: 10 merchants from the fine-jewelry / fancy-pantry / sweets-from-
 *  around-the-world / travel-inspired-home guides — each PAYS (US) with a traced
 *  network hop, and every measured cold click landed on the exact product
 *  (8–17 per merchant, iphone + desktop). diasporaco.com PAYS but is left out:
 *  its wrapped click lands on the homepage (Impact drops the deep link).
 *  2026-10-28: 26 catalog merchants from the monetization "Switch on" list
 *  (plus oakessentials.com from the un-boring-lotion guide) — each PAYS (US),
 *  traces through an affiliate network (Impact, Awin, Rakuten, CJ, AvantLink),
 *  and every measured cold click landed on the exact product with network
 *  tracking on the landing URL (8–16 per merchant, iphone + desktop; pocampo.com
 *  bot-blocks headless browsers, so it was confirmed in a real browser). Left
 *  out: timex.com (CJ drops the deep link — homepage), shop-us.taschen.com
 *  (lands on taschen.com/en/), and stores whose wrapped click reaches the
 *  merchant with no affiliate network in between (no tracking = no pay).
 *  2026-10-28: abercrombie.com (NFL-by-Abercrombie, for the NFL fan guides) — PAYS
 *  (US, 15% rate-card figure), viglink > Impact > the product page with network
 *  tracking on the landing URL. Measured on two /shop/us/p/ sweatshirt product
 *  pages, each plain and with ?seq= / ?categoryId=&faceout=&seq= queries: 32/32
 *  wrapped cold clicks kept the product path (iphone + desktop), plain control
 *  16/16. Headless browsers only see Abercrombie's bot "Client Challenge" page
 *  (wrapped and plain alike), so the rendered product was confirmed in a real
 *  Chrome. Not measured: collection/category pages, kids or non-US locales, iOS
 *  app handoff.
 *  2026-11-02: neweracap.com (NBA women's caps + crews, for the NBA fan guides) —
 *  PAYS (US, 4% rate-card figure), viglink > Rakuten (click.linksynergy.com) > the
 *  product page with ranMID/ranEAID/ranSiteID on the landing URL. Measured on two
 *  /products/ pages, each plain and with Shopify ?_pos=&_sid=&_ss= search params:
 *  32/32 wrapped cold clicks kept the product path (iphone + desktop), all 32 with
 *  Rakuten tracking; plain control 16/16; headless renders the product page (no
 *  bot challenge). Not measured: collection pages, iOS app handoff. */
export const SOVRN_ALLOWED_HOSTS: ReadonlySet<string> = new Set([
  '2modern.com',
  '47brand.com',
  'abacussportswearus.com',
  'abbode.com',
  'abercrombie.com',
  'adidas.com',
  'agedandore.com',
  'ahbabyco.com',
  'alabasterco.com',
  'alexandani.com',
  'aloyoga.com',
  'amazingco.me',
  'americaandbeyond.com',
  'americangirl.com',
  'anthropologie.com',
  'arjunadesignstudio.com',
  'armra.com',
  'artofplay.com',
  'artoftea.com',
  'aryandmaan.com',
  'assouline.com',
  'athleticbrewing.com',
  'atlascoffeeclub.com',
  'awaytravel.com',
  'awoopets.com',
  'ba-sh.com',
  'babiators.com',
  'babyriddle.com',
  'badbirdiegolf.com',
  'bakedbymelissa.com',
  'ballerinafarm.com',
  'bananarepublicfactory.gapfactory.com',
  'bando.com',
  'barefootdreams.com',
  'barkbistro.com',
  'barnesandnoble.com',
  'basketpong.com',
  'battlesports.com',
  'baublebar.com',
  'beachriot.com',
  'beanbox.com',
  'beekman1802.com',
  'bellabubear.com',
  'bespokepost.com',
  'beveragemixers.com',
  'biggerthanbasketball.co',
  'binkybro.com',
  'birdies.com',
  'bl101.com',
  'blazepod.com',
  'bleachercreatures.com',
  'bloomingdales.com',
  'bloomist.com',
  'bloomsybox.com',
  'bluebottlecoffee.com',
  'blueland.com',
  'boandmei.com',
  'boarderie.com',
  'bokksu.com',
  'bonjourfete.com',
  'bonneetfilou.com',
  'bookofthemonth.com',
  'bourbonbarrelfoods.com',
  'boxlunch.com',
  'brightland.co',
  'brookandyork.com',
  'brooklinen.com',
  'brooklyndelhi.com',
  'brooksbrothers.com',
  'brunelbeauty.com',
  'bubbarose.com',
  'bucketsquad.com',
  'bucktownseed.com',
  'buildabear.com',
  'burlapandbarrel.com',
  'burtsbeesbaby.com',
  'businessandpleasureco.com',
  'byloftie.com',
  'bysimran.com',
  'cacaoandcardamom.com',
  'cadenlane.com',
  'caitlynminimalist.com',
  'cambridgeuncommon.com',
  'campsnapphoto.com',
  'canfishcam.com',
  'caskcartel.com',
  'catbirdnyc.com',
  'catepaperco.com',
  'cb2.com',
  'cecilandlou.com',
  'cheekyplum.com',
  'chillhouse.com',
  'circuitmess.com',
  'classicwhimsy.com',
  'cleobella.com',
  'clevrblends.com',
  'cocokind.com',
  'coloredorganics.com',
  'comfrt.com',
  'compartes.com',
  'cosmiccatcoffeeco.com',
  'cosmody.shop',
  'craftbeerclub.com',
  'craftutsav.com',
  'craighill.co',
  'crateandbarrel.com',
  'cratejoy.com',
  'crocs.com',
  'cultiverre.com',
  'd-louise.com',
  'dandelionchocolate.com',
  'dateboxclub.com',
  'davidstea.com',
  'dearannabelle.com',
  'denydesigns.com',
  'diamondartclub.com',
  'ditalia.com',
  'dogeared.com',
  'doughparlour.com',
  'drinkag1.com',
  'duckhead.com',
  'duffieldlane.com',
  'duluthtrading.com',
  'eatfishwife.com',
  'ebbets.com',
  'ecreamery.com',
  'elfcosmetics.com',
  'ember.com',
  'emijay.com',
  'envirogardening.com',
  'ettika.com',
  'everlane.com',
  'everlastingcandleco.com',
  'fableengland.com',
  'fablepets.com',
  'fahertybrand.com',
  'fanatics.com',
  'faoschwarz.com',
  'farmrio.com',
  'fatbraintoys.com',
  'fellowproducts.com',
  'filson.com',
  'filthyfood.com',
  'finishline.com',
  'firehousepetshop.com',
  'flamingoestate.com',
  'flaviar.com',
  'flaxandtwine.com',
  'flewdstresscare.com',
  'flybyjing.com',
  'framebridge.com',
  'fredericksandmae.com',
  'freepeople.com',
  'frostbuddy.com',
  'fultonfishmarket.com',
  'furbishstudio.com',
  'furbo.com',
  'galison.com',
  'gap.com',
  'gapfactory.com',
  'gardenheir.com',
  'gathre.com',
  'getbrick.app',
  'getmechanism.com',
  'getpottd.com',
  'getsuperspace.com',
  'givethembeer.com',
  'glamsquad.com',
  'glance-led.com',
  'global.hungryminds.com',
  'globalgrub.com',
  'glossier.com',
  'goatusa.com',
  'goldbelly.com',
  'gorjana.com',
  'gourmetgiftbaskets.com',
  'graphicimage.com',
  'gratsi.com',
  'graza.co',
  'grillmastersclub.com',
  'habausa.com',
  'halfdaytravel.com',
  'hannaandersson.com',
  'harleylilac.com',
  'harney.com',
  'hatchcollection.com',
  'heathceramics.com',
  'hedleyandbennett.com',
  'hellonoemie.com',
  'herespa.com',
  'herocosmetics.us',
  'hillhousehome.com',
  'homage.com',
  'homesick.com',
  'homewetbar.com',
  'hoorayheroes.com',
  'hopscotchstore.com',
  'hottopic.com',
  'hunterboots.com',
  'hydrow.com',
  'inaury.com',
  'indoorhooper.com',
  'inkalloy.com',
  'inmyjammers.com',
  'insighteditions.com',
  'intelligentchange.com',
  'ippodotea.com',
  'iseeme.com',
  'janieandjack.com',
  'jazeena.com',
  'jenis.com',
  'jenny-bird.com',
  'jennylemons.com',
  'jonathanadler.com',
  'joyrealtoys.com',
  'juniperbooks.com',
  'kanefootwear.com',
  'katemcleod.com',
  'kendrascott.com',
  'kicksshoelaces.com',
  'kieljamespatrick.com',
  'kikkerland.com',
  'kimcmarket.com',
  'kinnstudio.com',
  'kiwico.com',
  'knittybaby.com',
  'koio.co',
  'kokorocares.com',
  'kromawellness.com',
  'kyteliving.com',
  'laboiteny.com',
  'laduree.com',
  'lakepajamas.com',
  'lakeshorelearning.com',
  'lambwolf.co',
  'leatherology.com',
  'legends.com',
  'lego.com',
  'lelabofragrances.com',
  'lelesadoughi.com',
  'levainbakery.com',
  'limeandlou.com',
  'lisaangel.co.uk',
  'lisilerch.com',
  'literiecandle.com',
  'littlechickenkids.com',
  'littlesaints.com',
  'littlesleepies.com',
  'littlestockingco.com',
  'littletrouble.com',
  'littleyogasocks.com',
  'loefflerrandall.com',
  'lolaandtheboys.com',
  'lorenacanals.us',
  'lovevery.com',
  'lukeslobster.com',
  'lulasgarden.com',
  'lunya.co',
  'madebymary.com',
  'magneticme.com',
  'magnolia.com',
  'maisonette.com',
  'manoswine.com',
  'mantrabrand.com',
  'manyavar.com',
  'marble-lotus.com',
  'markandgraham.com',
  'markhamandfitz.com',
  'markys.com',
  'masterclass.com',
  'materialkitchen.com',
  'maxbone.com',
  'maxiscreations.com',
  'melaartisans.com',
  'melissaanddoug.com',
  'merimeri.com',
  'mgemi.com',
  'milkbarstore.com',
  'minidreamers.com',
  'minted.com',
  'moderndesi.com',
  'modernsprout.com',
  'moderntribe.com',
  'modestvintageplayer.com',
  'montessorigeneration.com',
  'moonkieshop.com',
  'mrboddington.com',
  'mrchocolate.com',
  'mrsalice.com',
  'mudpie.com',
  'mudpuppy.com',
  'mycalmi.com',
  'mydollfy.com',
  'myfahlo.com',
  'myfairmahjong.com',
  'mykitsch.com',
  'nautiluspuzzles.com',
  'nestnewyork.com',
  'neweracap.com',
  'nike.com',
  'nobodyschild.com',
  'nordicpeace.com',
  'nordstrom.com',
  'nuggetcomfort.com',
  'nunonalife.com',
  'nuuly.com',
  'oakandluna.com',
  'oakessentials.com',
  'oaki.com',
  'odinparker.com',
  'oglmove.com',
  'ohmymahjong.com',
  'ombraz.com',
  'ooni.com',
  'osoandme.com',
  'owenandsage.com',
  'pacifierkids.com',
  'packit.com',
  'papersource.com',
  'papier.com',
  'pappyco.com',
  'parachutehome.com',
  'parcelair.co',
  'patternbeauty.com',
  'pbteen.com',
  'peacelovelightshop.com',
  'peachandlily.com',
  'pehr.com',
  'personal-prints.com',
  'personalize.williams-sonoma.com',
  'pfcandleco.com',
  'picassotiles.com',
  'piccalio.com',
  'picnictime.com',
  'pieceworkpuzzles.com',
  'pilatesflo.com',
  'pinchprovisions.com',
  'pippipost.com',
  'pippoppost.com',
  'pjsalvage.com',
  'platecrate.com',
  'playbookproducts.com',
  'pocampo.com',
  'pococo.com',
  'pokemoncenter.com',
  'popashot.net',
  'poppypine.com',
  'potterybarn.com',
  'potterybarnkids.com',
  'primallypure.com',
  'propercloth.com',
  'q30.com',
  'qbouncesport.com',
  'quince.com',
  'rangerstation.co',
  'redroostercoffee.com',
  'reedsmythe.com',
  'reigningchamp.com',
  'representclo.com',
  'retrospec.com',
  'revolve.com',
  'revomadic.com',
  'reytoz.com',
  'riflepaperco.com',
  'riverandstonetea.com',
  'robotimeonline.com',
  'roughlinen.com',
  'rouje.com',
  'rowdysprout.com',
  'rufflebutts.com',
  'runamokmaple.com',
  'ryleeandcru.com',
  'saiehello.com',
  'saltandstone.com',
  'sandgrainstudio.com',
  'schaeferoutfitter.com',
  'sculpd.com',
  'seasonjournals.com',
  'seatopia.fish',
  'seavees.com',
  'senseball.com',
  'sephora.com',
  'sezane.com',
  'sfoglini.com',
  'sharperimage.com',
  'shop-salchicha.com',
  'shop.emilylex.com',
  'shop.getty.edu',
  'shop.kulturekhazana.com',
  'shop.lospoblanos.com',
  'shop.minimuseum.com',
  'shopbala.com',
  'shopflowerlane.com',
  'shopfreshwater.com',
  'shopgreatpretenders.com',
  'shoplittleladoo.com',
  'shopmachete.com',
  'shopminnow.com',
  'shoppatet.com',
  'shopterrain.com',
  'showmeyourmumu.com',
  'sklz.com',
  'skultuna.com',
  'smallerthings.com',
  'snakeriverfarms.com',
  'soarbb.com',
  'sockittome.com',
  'sophieratner.com',
  'spicewallabrand.com',
  'splits59.com',
  'spoonfulofcomfort.com',
  'springlandpets.com',
  'statebags.com',
  'stelleworld.com',
  'stitchyfish.com',
  'store.177milkstreet.com',
  'store.moma.org',
  'store.nytimes.com',
  'store.ussoccer.com',
  'storybug.com',
  'sugoimart.com',
  'sunnylife.com',
  'sunshinetienda.com',
  'supersmalls.com',
  'sweetwaterdecor.com',
  'swiglife.com',
  'sydneyevan.com',
  'taratreasures.com',
  'target.com',
  'taschen.com',
  'taylorstitch.com',
  'teabeasties.com',
  'teachersource.com',
  'teamprostandard.com',
  'teaspressa.com',
  'tenderleaftoys.com',
  'tezumi.com',
  'the-citizenry.com',
  'theadventurechallenge.com',
  'thechaibox.com',
  'thefloralsociety.com',
  'thefoggydog.com',
  'theheritagesupplyco.com',
  'themousemansion.com',
  'theoffbits.com',
  'therabody.com',
  'thesill.com',
  'theworksbrands.com',
  'thezeroproof.com',
  'thingsremembered.com',
  'throwmotrainer.com',
  'tidal-tank.com',
  'tincan.kids',
  'tinggly.com',
  'tnuck.com',
  'tobioskits.com',
  'todaytix.com',
  'tommyjohn.com',
  'tonal.com',
  'topgolf.com',
  'topodesigns.com',
  'truewerk.com',
  'tsukiglass.com',
  'tubbytodd.com',
  'tula.com',
  'umestudio.com',
  'unboxme.com',
  'unclenearest.reservebar.com',
  'uncommongoods.com',
  'underarmour.com',
  'uniqlo.com',
  'uniquemaps.com',
  'urbangeneralstore.com',
  'urbanoutfitters.com',
  'urbanstems.com',
  'us.astridandmiyu.com',
  'us.boden.com',
  'us.craftly.com',
  'us.famwalls.com',
  'us.hotelchocolat.com',
  'us.laneige.com',
  'us.letterfest.com',
  'us.missoma.com',
  'us.omy-maison.com',
  'us.puma.com',
  'us.schleich-s.com',
  'us.sunspel.com',
  'us.tbco.com',
  'us.toa.st',
  'usa.spell.co',
  'varahgourmet.com',
  'varley.com',
  'vellabox.com',
  'velvet-tees.com',
  'venusetfleur.com',
  'verabradley.com',
  'veronicabeard.com',
  'vettexsports.com',
  'vineyardvines.com',
  'viski.com',
  'vitruvi.com',
  'voluspa.com',
  'vuoriclothing.com',
  'warmies.com',
  'wazoogear.com',
  'weezietowels.com',
  'westelm.com',
  'wildgrain.com',
  'wildone.com',
  'williams-sonoma.com',
  'wilson.com',
  'winechateau.com',
  'wonderandwise.com',
  'wonderbly.com',
  'worldofforgrave.com',
  'wufers.com',
  'www2.hm.com',
  'yappyhourbev.com',
  'yearandday.com',
  'yeti.com',
  'yorkathleticsmfg.com',
  'youngwildandfriedman.com',
  'yuti.co',
  'zumbamboo.com',
]);

/**
 * Confirmed Sovrn-broken. Anything not on the allowlist gets the plain URL
 * anyway; this set exists for documentation/tests AND as the daily canary's
 * watch list — it keeps clicking these so a Sovrn-side fix fires a
 * "GOOD NEWS — re-add?" alert.
 * - etsy.com — canary 2026-07-30: Sovrn's per-click auction routes EVERY
 *   wrapped click through CPC arbitrage partners (redirect.viglink.com →
 *   linksredirect.com → linkbux.com) that discard the product deep link and
 *   land on etsy.com homepage — 0/40 clicks correct across 10 products on both
 *   UA profiles, while the plain-URL control landed the exact product 4/4.
 *   Same auction-routing failure shape that hit amazon.com 2026-07-27 (below);
 *   persists with the account's CPC merchant programs off. Escalated to the
 *   Sovrn rep 2026-07-30. The daily canary re-checks it.
 *
 * sephora.com, shop.getty.edu + uncommongoods.com were here until 2026-08-04.
 * (sephora.com had been listed since 2026-07-14: Sovrn routed it via a
 * search-arbitrage partner, pureleads, whose LinkShare storefront link
 * discarded the product deep link. The canary has measured it clean since
 * 2026-07-17.)
 * They are BACK ON THE BAKED ALLOWLIST — not because they were re-measured
 * once, but so the self-healing overlay can own them. The overlay only ever
 * SUBTRACTS from this list, so a host has to be ON it for the canary to
 * manage day-to-day; these three were deleted from it by a code change on
 * 2026-07-16, 18 days before the overlay existed (2026-08-03), which stranded
 * them in the human-gated path. The canary measured all three clean on
 * 2026-07-17 — the very next run — and every run since (18 of them), but the
 * only thing it could do was email.
 *
 * Why the removal was probably wrong in the first place: it was recorded as
 * "every wrapped click died at about:blank", which is exactly what
 * measure_affiliate_links.mjs produced when a navigation FAILED — its
 * `errored = !finalUrl` is false for the string 'about:blank', so a failed
 * goto classified as `other` (= merchant broke the deep link) instead of
 * `error`. Fixed in the hub 2026-08-04. Re-measured before this change:
 * 56/56 wrapped clicks correct across 14 products on both UA profiles.
 *
 * Risk shape: the overlay can only unwrap, so if any of these is genuinely
 * broken the canary auto-unwraps it within a day, no deploy and no human —
 * i.e. the worst case is today's behaviour, reached automatically.
 *
 * nordstrom.com was here 2026-07-06 → 2026-07-14 (Sovrn routed into
 * Nordstrom's closed affiliate program → siteclosed.nordstrom.com). Sovrn now
 * routes it via Impact (nordstrom.sjv.io) with deep links intact — re-added
 * to the allowlist after a clean re-measure (24/24 desktop clicks across 11
 * products + real-iPhone app deep-link). The daily canary re-checks it.
 *
 * amazon.com + a.co were here 2026-07-27 → 2026-07-28 (Sovrn's auction routed
 * EVERY amazon.com click to brixleybags.sjv.io — an unrelated merchant's
 * Impact link that 404s with "The link you clicked on is malformed" — 0/48
 * clicks correct across 12 products on both UA profiles). Sovrn fixed it on
 * their side; re-added after a clean re-measure 2026-07-28 (32/32 clicks
 * correct across 8 amazon.com products on both UA profiles, no brixleybags
 * hop anywhere in the chain; the 4 a.co short links landed on ASINs matching
 * their plain-link controls 4/4). The daily canary re-checks both.
 *
 * SUPERSEDED 2026-08-24: amazon.com + a.co are no longer Sovrn merchants. Thea
 * became a direct Amazon Associate (store id givethea-20) and now tags Amazon
 * links itself at click-out (openExternal → amazonAffiliate.ts). Both hosts
 * were removed from SOVRN_ALLOWED_HOSTS above; the hub Sovrn canary, which
 * parses that set, no longer measures them.
 */
export const SOVRN_BROKEN_HOSTS: ReadonlySet<string> = new Set([
  'etsy.com',
]);

function merchantHost(url: string): string {
  try {
    return new URL(url).hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return '';
  }
}

/**
 * Dynamic overlay of REMOVALS on top of the baked-in allowlist, written by
 * the daily Sovrn canary (kate629/thea scripts/sovrn_canary.py) to
 * `theaWebConfig/sovrnClickout` ({ removedHosts: string[] }) when it confirms
 * a merchant's Sovrn wrapping broke — and cleared again after the merchant
 * re-measures clean. Hydrated at app boot by useSovrnOverlay.
 *
 * The canary drives BOTH directions with no human in the loop:
 *   effective allowlist = (baked list + restoredHosts) - removedHosts
 *
 * SAFETY SHAPE: `restoredHosts` is bounded to SOVRN_BROKEN_HOSTS — the canary
 * can only ever bring back a merchant this code already knows about and is
 * already watching. It can NEVER wrap an arbitrary merchant, however the doc
 * is malformed or tampered with. Anything else in restoredHosts is ignored.
 *
 * Every other failure mode still falls to plain merchant URLs: a missing,
 * stale or unreadable doc leaves the baked list, and removedHosts is applied
 * LAST so an unwrap always beats a restore.
 *
 * The self-correcting loop: if a restore turns out to be wrong, the next
 * canary run measures the breakage and writes the host into removedHosts,
 * which wins. So a bad restore costs at most one run, and it costs plain
 * links — never broken wrapped ones.
 */
export const SOVRN_OVERLAY_COLLECTION = 'theaWebConfig';
export const SOVRN_OVERLAY_DOC = 'sovrnClickout';

let dynamicRemovedHosts: ReadonlySet<string> = new Set();
let dynamicRestoredHosts: ReadonlySet<string> = new Set();

const normalizeHost = (h: string) => h.toLowerCase().replace(/^www\./, '').trim();

/**
 * Replace the overlay (idempotent; pass [] to clear — tests use this too).
 *
 * `restoredHosts` is filtered to SOVRN_BROKEN_HOSTS on the way in: the canary
 * may only bring back a merchant this code already lists as broken, never
 * introduce a new one. That bound is enforced HERE rather than trusted from
 * the doc, so a malformed or hostile doc cannot widen what we wrap.
 */
export function applySovrnOverlay(
  removedHosts: string[],
  restoredHosts: string[] = [],
): void {
  dynamicRemovedHosts = new Set(removedHosts.map(normalizeHost));
  dynamicRestoredHosts = new Set(
    restoredHosts.map(normalizeHost).filter((h) => SOVRN_BROKEN_HOSTS.has(h)),
  );
}

/** Current overlay, for tests/diagnostics. */
export function appliedSovrnOverlay(): ReadonlySet<string> {
  return dynamicRemovedHosts;
}

/** Hosts the canary has automatically restored to wrapping. */
export function appliedSovrnRestores(): ReadonlySet<string> {
  return dynamicRestoredHosts;
}

/**
 * Resolve the click-out URL for a product: the Sovrn-wrapped `affiliateUrl`
 * when the merchant is on the measured-clean allowlist (and not currently
 * removed by the canary's dynamic overlay), otherwise the plain merchant
 * `url`.
 */
export function resolveClickoutUrl(p: {
  url?: string;
  affiliateUrl?: string;
}): string | undefined {
  if (!p.url) return p.url || p.affiliateUrl || undefined;
  const host = merchantHost(p.url);
  // (baked + restored) - removed. removedHosts is checked LAST so an unwrap
  // always beats a restore — the safe direction wins ties.
  const wrappable = SOVRN_ALLOWED_HOSTS.has(host) || dynamicRestoredHosts.has(host);
  if (
    p.affiliateUrl &&
    p.affiliateUrl.includes('redirect.viglink') &&
    wrappable &&
    !dynamicRemovedHosts.has(host)
  ) {
    return p.affiliateUrl;
  }
  return p.url;
}
